import React from "react";
import styled from "styled-components";
import { Form, Formik, FormikHelpers } from "formik";
import * as Yup from "yup";
import * as Sentry from "@sentry/react";
import TextLink from "../library/TextLink";
import { TextInputField } from "../library/Form";
import SubmitButton from "../library/Form/SubmitButton";
import RecruiterUser from "../../apiResources/RecruiterUser";
import EmployerUser from "../../apiResources/EmployerUser";
import Skeleton from "../library/Skeleton";
import SettingLabel from "./SettingLabel";

const NameSettingOption = <User extends RecruiterUser | EmployerUser>(
  props: Props<User>
) => {
  const {
    user,
    isEditing = false,
    disabled = false,
    isLoading = false,
    onEdit,
    onDone,
    onUserChange,
  } = props;

  const save = async (
    values: FormValues,
    formik: FormikHelpers<FormValues>
  ) => {
    if (
      values.firstName === user.firstName &&
      values.lastName === user.lastName
    ) {
      onDone();
      return;
    }

    try {
      user.assignAttributes(values);
      const success = await user.save();
      if (success) {
        onUserChange?.(user);
        onDone();
      } else {
        formik.setErrors({
          firstName: user.errors.firstName.fullMessage,
          lastName: user.errors.lastName.fullMessage,
        });
      }
    } catch (err) {
      Sentry.captureException(err);
    }
  };

  const edit = () => {
    if (!disabled) {
      onEdit();
    }
  };

  const showNotSpecified = !isLoading && !user?.fullName;
  const showName = !isLoading && Boolean(user?.fullName);

  if (!isEditing) {
    return (
      <section data-test="name-setting-option">
        <Header>
          <SettingLabel>Name</SettingLabel>
          <TextLink disabled={disabled} onClick={edit}>
            Edit
          </TextLink>
        </Header>
        {isLoading && <Skeleton height="22" />}
        {showNotSpecified && <div>Not specified</div>}
        {showName && <div>{user.fullName}</div>}
      </section>
    );
  }

  const initialValues: FormValues = {
    firstName: user.firstName,
    lastName: user.lastName,
  };

  const validation = Yup.object({
    firstName: Yup.string().required("Please enter a first name"),
    lastName: Yup.string().required("Please enter a last name"),
  });

  return (
    <section data-test="name-setting-option">
      <Formik
        initialValues={initialValues}
        onSubmit={save}
        validationSchema={validation}
      >
        <Form autoComplete="off">
          <Header>
            <SettingLabel>Name</SettingLabel>
            <TextLink onClick={onDone}>Cancel</TextLink>
          </Header>
          <InputRow>
            <TextInputField label="First name" name="firstName" />
            <TextInputField label="Last name" name="lastName" />
          </InputRow>
          <SubmitButton />
        </Form>
      </Formik>
    </section>
  );
};

type Props<User> = {
  user: User;
  isEditing?: boolean;
  disabled?: boolean;
  isLoading?: boolean;
  onEdit: () => void;
  onDone: () => void;
  onUserChange?: (user: User) => void;
};

type FormValues = {
  firstName: string;
  lastName: string;
};

const InputRow = styled.div`
  @media screen and (min-width: 500px) {
    grid-template-columns: 1fr 1fr;
    display: grid;
    grid-column-gap: 1rem;
  }
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  margin-bottom: 0.5rem;
`;

export default NameSettingOption;
